import { getModelQualityPointsCost } from '@/api/config';
import { useAppConfig } from '@/hooks/useAppConfig';

interface QualitySelectorProps {
  value: string;
  onChange: (q: string) => void;
  isVip: boolean;
  disabled?: boolean;
  modelId: string;
  supportedQualities?: string[];
}

export default function QualitySelector({ value, onChange, isVip, disabled, modelId, supportedQualities }: QualitySelectorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = useAppConfig();
  const [showVipGate, setShowVipGate] = useState(false);

  const getPointsCost = (q: string) => {
    return getModelQualityPointsCost(config, modelId, q);
  };

  const handleSelect = (q: string) => {
    if (disabled) return;
    if (q === 'ultra' && config?.ultra_vip_only === true && !isVip) {
      setShowVipGate(true);
      return;
    }
    onChange(q);
  };

  return (
    <>
      <div className="mt-2 space-y-2">
        {(supportedQualities ?? []).map((id) => {
          const isSelected = value === id;
          const isUltraLocked = id === 'ultra' && config?.ultra_vip_only === true && !isVip;
          const cost = getPointsCost(id);
          const name = t(`gen_quality_${id}_name`, { defaultValue: id });
          const description = t(`gen_quality_${id}_desc`, { defaultValue: '' });

          return (
            <button
              key={id}
              data-testid={`quality-item-${id}`}
              aria-label={`generation-quality-${id}`}
              onClick={() => handleSelect(id)}
              disabled={disabled}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all cursor-pointer active:scale-[0.98] ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              } ${isSelected ? 'ring-2 ring-pink-400' : ''}`}
              style={
                isSelected
                  ? { background: 'linear-gradient(135deg, #FF6B9D10, #A855F710)' }
                  : { background: '#F9FAFB' }
              }
            >
              <div className="flex-1 text-left">
                <div className="flex items-center gap-1.5">
                  {isUltraLocked && <i className="ri-vip-crown-fill text-amber-500 text-xs"></i>}
                  <span className={`text-sm font-semibold ${isSelected ? 'text-pink-500' : 'text-gray-800'}`}>
                    {name}
                  </span>
                  {cost !== null && (
                    <span className={`text-xs ${isSelected ? 'text-pink-400' : 'text-gray-400'}`}>
                      · {cost} {t('gen_points')}
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-0.5 ${isUltraLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                  {description}
                </p>
              </div>
              {isSelected && <i className="ri-check-line text-pink-500 ml-2 flex-shrink-0"></i>}
            </button>
          );
        })}
      </div>

      {showVipGate && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowVipGate(false)} />
          <div className="relative w-full bg-white rounded-t-3xl px-5 pt-6 animate-slide-up" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setShowVipGate(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                <i className="ri-vip-crown-2-fill text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800">{t('gen_quality_ultraVipOnly')}</h3>
              <p className="text-sm text-gray-500">{t('gen_quality_ultraVipDesc')}</p>
              <button
                onClick={() => { setShowVipGate(false); navigate('/membership'); }}
                className="w-full py-3 rounded-2xl text-white font-bold text-base mt-2 cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                {t('gen_quality_ultraVipBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
